import React, { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import { CaptivePortalTemplateProps } from '../types';
import { CaptivePortalContainer, isCaptivePortalFormDataValid } from './container';
import chroma from 'chroma-js';

export const CorporateTemplateDataKeys = [
  'logoUrl',
  'header',
  'content',
  'buttonText',
  'showName',
  'showEmail',
  'requireName',
  'requireEmail',
  'requireAcceptMarketing',
  'termsAndConditionsUrl',
  'privacyPolicyUrl',
  'cardBackgroundColor',
  'cardFieldTextColor',
  'cardFieldBorderColor',
  'cardTextColor',
  'cardHeaderColor',
  'buttonColor',
  'buttonTextColor',
  'primaryColor',
  'backgroundColor',
] as const;

export const CorporateTemplate: React.FC<
  CaptivePortalTemplateProps<(typeof CorporateTemplateDataKeys)[number]>
> = (props) => {
  const { data, formData, onFormChange, onSubmit, isSubmitting = false } = props;
  const { t } = useTranslation();

  const handleInputChange =
    (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onFormChange({
        ...formData,
        [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
      });
    };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isCaptivePortalFormDataValid(data, formData)) {
      onSubmit();
    }
  };

  return (
    <CaptivePortalContainer {...props}>
      <Box
        sx={{
          minHeight: '100vh',
          minWidth: '100vw',
          background: buildLightenedLinearGradientFromHex('#f5f7fa', data.backgroundColor),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            maxWidth: 800,
            width: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: data.cardBackgroundColor || '#fff',
          }}
        >
          <Grid container sx={{ minHeight: 400 }}>
            {/* Left side - Branding */}
            <Grid
              size={{ xs: 12, md: 5 }}
              sx={{
                background: buildLinearGradientFromHex('#667eea', data.buttonColor),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                color: 'white',
                textAlign: 'center',
              }}
            >
              {data.logoUrl && (
                <Box
                  component="img"
                  src={data.logoUrl}
                  alt="Logo"
                  sx={{
                    maxWidth: 120,
                    maxHeight: 80,
                    mb: 3,
                    objectFit: 'contain',
                  }}
                />
              )}
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                {data.header}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {data.content}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                sx={{ p: 4 }}
                height="100%"
              >
                <Box component="form" onSubmit={handleFormSubmit} noValidate>
                  {data.showName && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid size={{ xs: 6 }}>
                        <StyledTextField
                          data={data}
                          fullWidth
                          label={t('captivePortal.firstName', 'First Name')}
                          value={formData.firstName}
                          onChange={handleInputChange('firstName')}
                          required={data.requireName}
                          variant="outlined"
                          size="medium"
                        />
                      </Grid>
                      <Grid size={6}>
                        <StyledTextField
                          data={data}
                          fullWidth
                          label={t('captivePortal.lastName', 'Last Name')}
                          value={formData.lastName}
                          onChange={handleInputChange('lastName')}
                          required={data.requireName}
                          variant="outlined"
                          size="medium"
                        />
                      </Grid>
                    </Grid>
                  )}

                  {data.showEmail && (
                    <StyledTextField
                      data={data}
                      fullWidth
                      label={t('captivePortal.email', 'Email Address')}
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      required={data.requireEmail}
                      variant="outlined"
                      size="medium"
                      sx={{ mb: 3 }}
                    />
                  )}

                  {data.termsAndConditionsUrl && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.acceptedTerms || false}
                          onChange={handleInputChange('acceptedTerms')}
                          sx={{
                            color: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.23)',
                            '&.Mui-checked': {
                              color: data.cardFieldBorderColor || 'primary.main',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" color={data.cardTextColor || 'text.secondary'}>
                          {t('acceptTerms', 'I accept the')}{' '}
                          <Link
                            href={data.termsAndConditionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: data.cardTextColor || 'primary.main',
                              textDecoration: 'underline',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {t('termsConditions', 'Terms & Conditions')}
                          </Link>
                          {data.privacyPolicyUrl && (
                            <>
                              {' '}
                              {t('and', 'and')}{' '}
                              <Link
                                href={data.privacyPolicyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                {t('privacyPolicy', 'Privacy Policy')}
                              </Link>
                            </>
                          )}
                          .
                        </Typography>
                      }
                      sx={{ alignItems: 'center' }}
                    />
                  )}

                  {data.requireAcceptMarketing && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.acceptedMarketing || false}
                          onChange={handleInputChange('acceptedMarketing')}
                          sx={{
                            color: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.23)',
                            '&.Mui-checked': {
                              color: data.cardFieldBorderColor || 'primary.main',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" color={data.cardTextColor || 'text.secondary'}>
                          {t(
                            'acceptMarketing',
                            'I would like to receive marketing communications and promotional offers',
                          )}
                        </Typography>
                      }
                      sx={{ alignItems: 'flex-start' }}
                    />
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isSubmitting || !isCaptivePortalFormDataValid(data, formData)}
                    sx={{
                      py: 1.5,
                      mt: 3,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: buildLinearGradientFromHex('#667eea', data.buttonColor),
                      '&:hover': {
                        background: buildDarkenedLinearGradientFromHex('#667eea', data.buttonColor),
                      },
                      '&:disabled': {
                        opacity: 0.7,
                        color: data.buttonTextColor || 'white',
                      },
                      color: data.buttonTextColor || 'white',
                    }}
                  >
                    {isSubmitting
                      ? t('captivePortal.connecting', 'Connecting...')
                      : data.buttonText || t('captivePortal.connect', 'Connect to Internet')}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </CaptivePortalContainer>
  );
};

function StyledTextField(
  props: ComponentProps<typeof TextField> & {
    data: CaptivePortalTemplateProps<(typeof CorporateTemplateDataKeys)[number]>['data'];
  },
) {
  const { data, ...rest } = props;
  return (
    <TextField
      {...rest}
      sx={{
        ...rest.sx,
        '& .MuiInputLabel-root': {
          color: data.cardFieldTextColor || 'text.primary',
        },
        '& .MuiInputBase-input': {
          color: data.cardFieldTextColor || 'text.primary',
        },
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover fieldset': {
            borderColor: data.cardFieldBorderColor || 'rgba(0, 0, 0, 0.87)',
          },
          '&.Mui-focused fieldset': {
            borderColor: data.buttonColor || data.primaryColor || 'primary.main',
          },
        },
      }}
    />
  );
}

function buildLightenedLinearGradientFromHex(defaultColor: string, color?: string): string {
  color = color || defaultColor;
  const color1 = chroma(color).brighten(0.75).hex();
  return `linear-gradient(135deg, ${color} 0%, ${color1} 100%)`;
}

function buildLinearGradientFromHex(defaultColor: string, color?: string): string {
  color = color || defaultColor;
  const color1 = chroma(color).darken(1.5).hex();
  return `linear-gradient(135deg, ${color} 0%, ${color1} 100%)`;
}

function buildDarkenedLinearGradientFromHex(defaultColor: string, color?: string): string {
  color = color || defaultColor;
  const color1 = chroma(color).darken(0.5).hex();
  const color2 = chroma(color).darken(1.5).hex();
  return `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`;
}
